import useTimerStore from "@/utils/Stores/timerStore";
import { useEffect, useRef, useState } from "react";
import { FaPlayCircle, FaPauseCircle, FaStopCircle } from "react-icons/fa";

import Modal from "../Global/Modal";
import AddTimeLog from "../Timesheet/AddTimeLog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { successNotfiy } from "../Global/Toast";
import { serverPOST } from "@/utils/HttpFunctions";
import { errorNotfiy } from "../Global/Toast";
import Loader from "../Global/Loader";
import useUserStore from "@/utils/Stores/userStore";
import useWindowSize from "@/utils/hooks/useWindowSize";

const Timer = () => {
  const { isMobile } = useWindowSize();
  const queryClient = useQueryClient();
  const user = useUserStore((state: any) => state.user);

  const isStarted = useTimerStore((state: any) => state.isStarted);
  const setIsStarted = useTimerStore((state: any) => state.setIsStarted);
  const [addTasks, setAddTasks] = useState(false);
  const selectedProject = useTimerStore((state: any) => state.selectedProject);
  const setSelectedProject = useTimerStore(
    (state: any) => state.setSelectedProject
  );
  const timerDescription = useTimerStore(
    (state: any) => state.timerDescription
  );
  const setTimerDescription = useTimerStore(
    (state: any) => state.setTimerDescription
  );
  // const time = useTimerStore((state: any) => state.time); // Time in seconds
  // const setTime = useTimerStore((state: any) => state.setTime); // Time in seconds
  const [time, setTime] = useState(0);
  const inactivityTimeoutRef = useRef(null);

  const INACTIVITY_TIME = 5 * 60 * 1000; // 5 minutes

  const togglePlayPause = () => {
    if (!isPending) {
      if (selectedProject) {
        setIsStarted(!isStarted);
      } else {
        setAddTasks(true);
      }
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (task: any) => {
      return serverPOST("/timesheet/timer", task);
    },
    onError: (error, variables, context: any) => {
      errorNotfiy(error?.message || "Something went wrong!");
    },
    onSuccess: async (updatedData: any, variables, context) => {
      successNotfiy("Time entry has been added successfully");
      if (window.location.pathname.includes("timesheet")) {
        queryClient.invalidateQueries({
          queryKey: ["user-timesheet", user?._id],
        });
      }

      setTime(0);
      setSelectedProject("");
      setTimerDescription("");
    },
  });

  // Cleanup interval on component unmount
  useEffect(() => {
    let timer;
    if (isStarted) {
      timer = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
      resetInactivityTimer();
      window.addEventListener("mousemove", resetInactivityTimer);
      window.addEventListener("keydown", resetInactivityTimer);
      window.addEventListener("click", resetInactivityTimer);
      window.addEventListener("pagehide", handleUnload); // Stop on refresh/close
    } else {
      clearInterval(timer);
    }
    return () => {
      clearInterval(timer);
      window.removeEventListener("mousemove", resetInactivityTimer);
      window.removeEventListener("keydown", resetInactivityTimer);
      window.removeEventListener("click", resetInactivityTimer);
      // Stop timer when page is refreshed, tab is closed, or user navigates away
      window.removeEventListener("pagehide", handleUnload);
      clearTimeout(inactivityTimeoutRef.current);
    };
  }, [isStarted]);

  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimeoutRef.current);
    inactivityTimeoutRef.current = setTimeout(() => {
      setIsStarted(false);
    }, INACTIVITY_TIME);
  };

  const formatTime = (seconds) => {
    const hrs = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };
  const handleClose = (isTimer) => {
    setAddTasks(false);
    if (isTimer) {
      setIsStarted(true);
    } else {
      setSelectedProject("");
    }
  };
  const handleUnload = () => {
    handleStop();
  };

  const handleStop = () => {
    setIsStarted(false);
    if (Math.round(time / 60) >= 1) {
      mutate({
        project: selectedProject.value,
        durationMinutes: Math.round(time / 60),
        date: new Date().getTime(),
        description: timerDescription,
        startDate: new Date(new Date().setHours(0, 0, 0, 0)).getTime(),
        endDate: new Date(new Date().setHours(23, 59, 59, 999)).getTime(),
      });
    } else {
      setTime(0);
      setSelectedProject("");
      setTimerDescription("");
    }
  };
  return (
    <div className="relative">
      {addTasks && (
        <Modal
          handleClose={handleClose}
          title={`New Time Entry`}
          width={isMobile ? "100%" : "max-w-md"}
          central
          leftAlign={true}
          // description="Add tasks here"
        >
          <div className="mt-2">
            <AddTimeLog handleClose={handleClose} isTimer={true} />
          </div>
        </Modal>
      )}

      <div className="fixed bottom-20 right-0 m-4">
        <div
          className={` rounded-full mt-3 ${isStarted ? " w-[120px] transition-all duration-500 " : " w-[50px] "} h-[50px] overflow-hidden cursor-pointer `}
          onClick={togglePlayPause}
        >
          {isPending ? (
            <div className="flex items-center justify-center w-full h-full">
              <Loader />
            </div>
          ) : isStarted ? (
            <div className="flex items-center justify-start  gap-2 text-customGreen-primary bg-customGreen-dark p-1 rounded-full w-full h-full">
              <div className="text-3xl ">
                <FaPauseCircle className="w-full h-full" />
              </div>
              <span>{isStarted && formatTime(time)}</span>
            </div>
          ) : (
            <div className=" text-3xl ">
              <FaPlayCircle className="text-md text-customGreen-primary bg-customGreen-dark p-1 rounded-full w-full h-full" />
            </div>
          )}
        </div>
      </div>
      {selectedProject && time > 0 && !isPending && (
        <div
          className="fixed bottom-5 right-0 m-4 overflow-hidden cursor-pointer w-[50px] h-[50px]"
          onClick={(e) => {
            e.stopPropagation();
            handleStop();
          }}
        >
          <FaStopCircle className="text-3xl text-customGreen-primary bg-customGreen-dark p-1 rounded-full w-full h-full" />
        </div>
      )}
    </div>
  );
};

export default Timer;
